import React, { Component } from "react";
import axios from "axios";
import { Link, Navigate } from "react-router-dom";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import { apiErrorMessage } from "Helpers/ContentValidation";

import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { Card, Chip, Grid, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import InventoryIcon from "@mui/icons-material/Inventory2";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

/**
 * Brand pages: Value Nest, Happinesque, Happy Wings, Versele-Laga (and its
 * Happy Life / Lara sub-brands) plus ranges such as Bird Feeders.
 *
 * The existing "Private Brands" and "Trusted Brands" screens are untouched —
 * those manage the logo cards on the Brands and Partners pages.
 */
export default class BrandPages extends Component {
  state = {
    brands: [],
    per_page: 20,
    loading: false,
    notification: false,
    notiColor: "success",
    message: "",
    redirect: false,
  };

  componentDidMount() {
    this.fetchBrands();
  }

  /* =============================
     FETCH
  ============================= */
  fetchBrands = async () => {
    if (init() !== "success") return this.setState({ redirect: true });

    this.setState({ loading: true });

    try {
      // A brand list is short and the table paginates on the client, so one
      // request is enough.
      const response = await axios.get(`${Constants.getUrls.getAllBrands}?limit=200`);
      this.setState({ brands: response.data?.data?.docs || [], loading: false });
    } catch (error) {
      this.setState({ loading: false });
      this.notify("warning", apiErrorMessage(error, "Could not load brands"));
    }
  };

  notify = (notiColor, message) => {
    this.setState({ notification: true, notiColor, message });
    setTimeout(() => this.setState({ notification: false, message: "" }), 4000);
  };

  /* =============================
     STATUS
  ============================= */
  handleToggleStatus = async (brand) => {
    if (init() !== "success") return this.setState({ redirect: true });

    try {
      const response = await axios.patch(
        Constants.patchUrls.toggleBrandStatus + brand._id
      );
      this.notify("success", response.data?.message || "Status updated");
      this.fetchBrands();
    } catch (error) {
      this.notify("warning", apiErrorMessage(error, "Could not change the status"));
    }
  };

  /* =============================
     DELETE

     The API refuses while the brand still holds products or sub-brands, and
     reports how many. Only then is the cascade offered.
  ============================= */
  handleDelete = async (brand, cascade = false) => {
    if (init() !== "success") return this.setState({ redirect: true });

    if (
      !cascade &&
      !window.confirm(`Delete the brand page "${brand.name}"?`)
    ) {
      return;
    }

    try {
      const url =
        Constants.deleteUrls.deleteBrand + brand._id + (cascade ? "?cascade=true" : "");
      const response = await axios.delete(url);

      this.notify("success", response.data?.message || "Brand deleted");
      this.fetchBrands();
    } catch (error) {
      const data = error?.response?.data;

      if (error?.response?.status === 409 && !cascade) {
        const confirmed = window.confirm(
          `${data?.message}\n\nDelete "${brand.name}" together with its ` +
            `${data?.product_count || 0} product(s) and ` +
            `${data?.sub_brand_count || 0} sub-brand(s)?`
        );
        if (confirmed) return this.handleDelete(brand, true);
        return;
      }

      this.notify("warning", apiErrorMessage(error, "Could not delete the brand"));
    }
  };

  /* =============================
     RENDER
  ============================= */
  render() {
    const { brands, loading, redirect } = this.state;

    if (redirect) return <Navigate to="/signin" replace />;

    return (
      <DashboardLayout>
        <DashboardNavbar />

        <MDBox pt={6} pb={3}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Brand Pages
              </MDTypography>
              <MDTypography variant="caption" color="white">
                Page content for Value Nest, Happinesque, Happy Wings, Versele-Laga
                (Happy Life &amp; Lara) and product ranges
              </MDTypography>
            </MDBox>

            <Grid item sm={4} px={2} pt={2} align="right">
              <Link to="/addbrandpage">
                <MDButton variant="contained" size="small" color="primary">
                  Add Brand Page
                </MDButton>
              </Link>
            </Grid>

            <MDBox py={3}>
              {loading ? (
                <Grid align="center">
                  <h4>Loading...</h4>
                </Grid>
              ) : brands.length > 0 ? (
                <DataTable
                  canSearch
                  entriesPerPage={{ defaultValue: 20, entries: [10, 20, 50, 100] }}
                  table={{
                    columns: [
                      { Header: "S.No", accessor: "serial", width: "6%" },
                      { Header: "Logo", accessor: "logo" },
                      { Header: "Brand", accessor: "brand" },
                      { Header: "Type", accessor: "type" },
                      { Header: "Products", accessor: "products" },
                      { Header: "Status", accessor: "status" },
                      { Header: "Actions", accessor: "action" },
                    ],
                    rows: brands.map((brand, index) => ({
                      serial: index + 1,

                      logo: brand.logo ? (
                        <img
                          src={
                            brand.logo.startsWith("/uploads/")
                              ? Constants.imageDomain + brand.logo
                              : brand.logo
                          }
                          alt={brand.name}
                          style={{
                            height: 44,
                            width: 90,
                            objectFit: "contain",
                            borderRadius: 4,
                          }}
                        />
                      ) : (
                        "—"
                      ),

                      brand: (
                        <MDBox>
                          <MDTypography variant="button" fontWeight="medium" display="block">
                            {brand.name}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            /{brand.slug}
                            {brand.parent_brand
                              ? ` · under ${brand.parent_brand.name}`
                              : ""}
                          </MDTypography>
                        </MDBox>
                      ),

                      type: (
                        <Chip
                          size="small"
                          label={brand.group === "range" ? "Range" : "Brand"}
                          color={brand.group === "range" ? "default" : "info"}
                          variant="outlined"
                        />
                      ),

                      products: (
                        <Link to={`/brandproducts?brand=${brand._id}`}>
                          <MDTypography variant="button" color="info" fontWeight="medium">
                            {brand.product_count ?? 0}
                          </MDTypography>
                        </Link>
                      ),

                      status: (
                        <Chip
                          size="small"
                          label={brand.status === "active" ? "Active" : "Inactive"}
                          color={brand.status === "active" ? "success" : "default"}
                        />
                      ),

                      action: (
                        <div style={{ display: "flex", gap: 6 }}>
                          <Tooltip title="Manage products">
                            <Link to={`/brandproducts?brand=${brand._id}`}>
                              <IconButton size="small" color="secondary">
                                <InventoryIcon fontSize="small" />
                              </IconButton>
                            </Link>
                          </Tooltip>

                          <Tooltip title="Edit brand page">
                            <Link to={`/updatebrandpage/${brand._id}`}>
                              <IconButton size="small" color="info">
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Link>
                          </Tooltip>

                          <Tooltip
                            title={
                              brand.status === "active"
                                ? "Hide from the website"
                                : "Show on the website"
                            }
                          >
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => this.handleToggleStatus(brand)}
                            >
                              {brand.status === "active" ? (
                                <VisibilityIcon fontSize="small" />
                              ) : (
                                <VisibilityOffIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete brand page">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => this.handleDelete(brand)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      ),
                    })),
                  }}
                />
              ) : (
                <div style={{ padding: 20 }}>No Brand Pages Found!</div>
              )}
            </MDBox>

            <MDSnackbar
              color={this.state.notiColor}
              icon="notifications"
              title="Brand Pages"
              content={this.state.message}
              dateTime="Just now"
              open={this.state.notification}
              close={() => this.setState({ notification: false })}
            />
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }
}
